import { useState } from "react";
import { validateReason } from "../../api/endOfDay.js";

/**
 * End of day state shared by the desktop and the phone screen (SPEC-CIS-COCKPIT-
 * DAY-REDESIGN-P2-001 2.1): which records are ticked (all, until the site
 * manager unticks one), which postponed sides are kept on today (none, until
 * "Keep on today"), the "Not attended" reason being typed, and the one-line
 * outcome of the last action.
 *
 *   onCloseSide(side, close, reason) -> { kind, message }   carry or mark one side now
 *   onSubmit({ records, carries })   -> { kind, approved, carried, message }
 */
export function useEndOfDay({ model, onCloseSide, onSubmit }) {
  const [unticked, setUnticked] = useState(() => new Set());
  const [kept, setKept] = useState(() => new Set());
  const [reasonKey, setReasonKey] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [pendingKey, setPendingKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null); // { tone: "ok" | "warn", text }

  const ticked = model.records.filter((r) => !unticked.has(r.key));
  const carries = model.postponed.filter((g) => !kept.has(g.key)).flatMap((g) => g.sides);
  const allTicked = model.records.length > 0 && ticked.length === model.records.length;

  function toggleRecord(key) {
    setUnticked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  function toggleAll() {
    setUnticked(allTicked ? new Set(model.records.map((r) => r.key)) : new Set());
  }
  function toggleKeep(key) {
    setKept((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function noteFrom(outcome, okText) {
    if (outcome && outcome.kind === "ok") return { tone: "ok", text: okText };
    return { tone: "warn", text: (outcome && outcome.message) || "The day could not be updated." };
  }

  async function carry(side) {
    if (pendingKey || submitting) return;
    setPendingKey(side.key);
    setMessage(null);
    let outcome;
    try {
      outcome = await onCloseSide(side, "carry");
    } catch (err) {
      outcome = { kind: "error", message: (err && err.message) || "request failed" };
    }
    setPendingKey(null);
    setMessage(noteFrom(outcome, `${side.title} is carried to ${model.carryLabel}.`));
  }

  function openReason(side) {
    setReasonKey(side.key);
    setReasonText("");
    setReasonError("");
  }
  function closeReason() {
    setReasonKey(null);
    setReasonText("");
    setReasonError("");
  }
  async function confirmReason(side) {
    const check = validateReason(reasonText);
    if (!check.ok) {
      setReasonError(check.message);
      return;
    }
    if (pendingKey || submitting) return;
    setPendingKey(side.key);
    setMessage(null);
    let outcome;
    try {
      outcome = await onCloseSide(side, "not_attended", check.reason);
    } catch (err) {
      outcome = { kind: "error", message: (err && err.message) || "request failed" };
    }
    setPendingKey(null);
    if (outcome && outcome.kind === "ok") closeReason();
    setMessage(noteFrom(outcome, `${side.title} is marked not attended.`));
  }

  async function submit() {
    if (submitting || pendingKey || (!ticked.length && !carries.length)) return;
    setSubmitting(true);
    setMessage(null);
    let outcome;
    try {
      outcome = await onSubmit({ records: ticked, carries });
    } catch (err) {
      outcome = { kind: "error", approved: 0, carried: 0, message: (err && err.message) || "request failed" };
    }
    setSubmitting(false);
    if (outcome && outcome.kind === "ok") {
      const parts = [];
      if (outcome.approved) parts.push(`${outcome.approved} ${outcome.approved === 1 ? "record" : "records"} approved`);
      if (outcome.carried) parts.push(`${outcome.carried} ${outcome.carried === 1 ? "side" : "sides"} carried to ${model.carryLabel}`);
      setMessage({ tone: "ok", text: `${parts.join(". ")}.` });
    } else {
      setMessage(noteFrom(outcome, ""));
    }
  }

  return {
    ticked, carries, allTicked, unticked, kept,
    reasonKey, reasonText, setReasonText, reasonError, pendingKey, submitting, message,
    toggleRecord, toggleAll, toggleKeep, carry, openReason, closeReason, confirmReason, submit,
    submitLabel: `Approve ${ticked.length} ${ticked.length === 1 ? "record" : "records"}`,
    canSubmit: !submitting && !pendingKey && (ticked.length > 0 || carries.length > 0),
  };
}
